import { ComponentConfig } from '@measured/puck';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import CustomTooltip from '../../../components/common/tooltip';
import { MonacoHTMLEditor } from '../../puck/reusable-props/monaco-html-editor';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { ReduxSelectorProps } from '../../services/constants';
import {
  marginFields,
  marginProps,
  MarginProps,
} from '../../puck/reusable-props/NewMargin';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../puck/reusable-props/widthAndHeight';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';

export type CustomHTMLProps = {
  htmlContent: string;
  widthAndHeight: WidthAndHeightSubProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  border: IBorderValueProps;
} & showComponentProps &
  PaddingProps &
  MarginProps;

export const CustomHTML = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<CustomHTMLProps> => {
  return {
    label: (
      <CustomTooltip title="CustomHTML">
        <span>Custom HTML</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      htmlContent: {
        ...MonacoHTMLEditor().htmlContent,
      },
      widthAndHeight: {
        type: 'custom',
        label: 'Width and Height',
        render: ({ value, onChange }) => {
          return (
            <WidthAndHeight
              value={value}
              onChange={onChange}
              title={'Width and Height'}
            />
          );
        },
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      htmlContent:
        '<div style="text-align: center; padding: 20px;"><h2 style="color: #333;">Custom HTML Content</h2><p style="font-size: 16px; line-height: 1.5;">Edit this HTML code with inline CSS styles</p></div>',
      background: { color: '#ffffff', isInherit: false },
      widthAndHeight: {
        height: {
          desktop: 'auto',
          mobile: 'auto',
          tablet: 'auto',
        },
        maxWidth: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
        deviceType: 'desktop',
      },
      ...marginProps,
      ...paddingProps,
      ...borderProps,
    },
    render: ({
      showComponent,
      htmlContent,
      widthAndHeight,
      margin,
      padding,
      background,
      border,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const borderStyles = getBorderProps(border, currentDeviceType);
      const widthAndHeightStyles: React.CSSProperties =
        currentDeviceType &&
        widthAndHeight?.maxWidth &&
        widthAndHeight?.height &&
        widthAndHeight?.maxWidth[currentDeviceType] !== undefined &&
        widthAndHeight?.height[currentDeviceType] !== undefined
          ? {
              width: isNaN(+widthAndHeight?.maxWidth[currentDeviceType])
                ? widthAndHeight?.maxWidth[currentDeviceType]
                : `${+widthAndHeight?.maxWidth[currentDeviceType]}px`,
              height: isNaN(+widthAndHeight?.height[currentDeviceType])
                ? widthAndHeight?.height[currentDeviceType]
                : `${+widthAndHeight?.height[currentDeviceType]}px`,
            }
          : {};
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`${border?.borderStyle?.[currentDeviceType]}`}
              style={{
                ...marginStyles,
                ...paddingStyles,
                ...widthAndHeightStyles,
                ...borderStyles,
                backgroundColor: background.isInherit
                  ? 'inherit'
                  : background.color,
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
          )}
        </>
      );
    },
  };
};
